/* eslint-disable @typescript-eslint/no-explicit-any */
import { Health } from "@capgo/capacitor-health";

/**
 * Test @capgo/capacitor-health v8.2.9 functionality
 * This tests all the data types we actively use in production
 */
export const testCapgoHealthV8 = async () => {
  console.log("🧪 Testing @capgo/capacitor-health v8.2.9...");

  const recordCounts: Record<string, number> = {};

  try {
    // Test 1: Availability
    const available = await Health.isAvailable();
    console.log("✓ isAvailable:", available);

    if (!available.available) {
      throw new Error("Health API not available on this device");
    }

    // Test 2: Request Authorization
    console.log("📋 Requesting permissions...");
    const authResult = await Health.requestAuthorization({
      read: [
        "steps",
        "heartRate",
        "restingHeartRate",
        "sleep",
        "oxygenSaturation",
        "respiratoryRate",
        "heartRateVariability",
        "distance",
        "calories",
        "weight",
        // "workouts",
      ],
      write: [
       "weight"
      ],
    });
    console.log("✓ Authorization result:", authResult);

    // Test 3: Check authorization status
    const authStatus = await Health.checkAuthorization({
      read: [
        "steps",
        "heartRate",
        "sleep",
        "oxygenSaturation",
        "respiratoryRate",
        "restingHeartRate",
        "heartRateVariability",
        "distance",
        "calories",
        "weight",
        // "workouts"
      ],
    });
    console.log("✓ Authorization status:", authStatus);

    // Date range: last 7 days
    const endDate = new Date().toISOString();
    const startDate = new Date(
      Date.now() - 7 * 24 * 60 * 60 * 1000
    ).toISOString();

    console.log(`📅 Fetching data from ${startDate} to ${endDate}`);

    // Test 4: Fetch Steps
    try {
      const steps = await Health.readSamples({
        dataType: "steps",
        startDate,
        endDate,
        limit: 100,
      });
      const count = steps.samples?.length || 0;
      recordCounts["Steps"] = count;
      console.log(`\n✓ Steps: ${count} records`);
      if (steps.samples && steps.samples.length > 0) {
        console.log("  First 50 records:");
        steps.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Steps error:", error);
      recordCounts["Steps"] = 0;
    }

    // Test 5: Fetch Heart Rate
    try {
      const heartRate = await Health.readSamples({
        dataType: "heartRate",
        startDate,
        endDate,
        limit: 100,
      });
      const count = heartRate.samples?.length || 0;
      recordCounts["Heart Rate"] = count;
      console.log(`\n✓ Heart Rate: ${count} records`);
      if (heartRate.samples && heartRate.samples.length > 0) {
        console.log("  First 50 records:");
        heartRate.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Heart Rate error:", error);
      recordCounts["Heart Rate"] = 0;
    }

    // Test 6: Fetch Resting Heart Rate
    try {
      const restingHR = await Health.readSamples({
        dataType: "restingHeartRate",
        startDate,
        endDate,
        limit: 100,
      });
      const count = restingHR.samples?.length || 0;
      recordCounts["Resting Heart Rate"] = count;
      console.log(`\n✓ Resting Heart Rate: ${count} records`);
      if (restingHR.samples && restingHR.samples.length > 0) {
        console.log("  First 50 records:");
        restingHR.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Resting Heart Rate error:", error);
      recordCounts["Resting Heart Rate"] = 0;
    }

    // Test 7: Fetch Sleep
    try {
      const sleep = await Health.readSamples({
        dataType: "sleep",
        startDate,
        endDate,
        limit: 100,
      });
      const count = sleep.samples?.length || 0;
      recordCounts["Sleep"] = count;
      console.log(`\n✓ Sleep: ${count} records`);
      if (sleep.samples && sleep.samples.length > 0) {
        console.log("  First 50 records:");
        sleep.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Sleep error:", error);
      recordCounts["Sleep"] = 0;
    }

    // Test 8: Fetch Oxygen Saturation
    try {
      const oxygen = await Health.readSamples({
        dataType: "oxygenSaturation",
        startDate,
        endDate,
        limit: 100,
      });
      const count = oxygen.samples?.length || 0;
      recordCounts["Oxygen Saturation"] = count;
      console.log(`\n✓ Oxygen Saturation: ${count} records`);
      if (oxygen.samples && oxygen.samples.length > 0) {
        console.log("  First 50 records:");
        oxygen.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Oxygen Saturation error:", error);
      recordCounts["Oxygen Saturation"] = 0;
    }

    // Test 9: Fetch Respiratory Rate
    try {
      const respiratory = await Health.readSamples({
        dataType: "respiratoryRate",
        startDate,
        endDate,
        limit: 100,
      });
      const count = respiratory.samples?.length || 0;
      recordCounts["Respiratory Rate"] = count;
      console.log(`\n✓ Respiratory Rate: ${count} records`);
      if (respiratory.samples && respiratory.samples.length > 0) {
        console.log("  First 50 records:");
        respiratory.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Respiratory Rate error:", error);
      recordCounts["Respiratory Rate"] = 0;
    }

    // Test 10: Fetch Heart Rate Variability (NEW!)
    try {
      const hrv = await Health.readSamples({
        dataType: "heartRateVariability",
        startDate,
        endDate,
        limit: 100,
      });
      const count = hrv.samples?.length || 0;
      recordCounts["Heart Rate Variability"] = count;
      console.log(`\n✓ Heart Rate Variability (NEW): ${count} records`);
      if (hrv.samples && hrv.samples.length > 0) {
        console.log("  First 50 records:");
        hrv.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ HRV error:", error);
      recordCounts["Heart Rate Variability"] = 0;
    }

    // Test 11: Fetch Weight
    try {
      const weight = await Health.readSamples({
        dataType: "weight",
        startDate,
        endDate,
        limit: 100,
      });
      const count = weight.samples?.length || 0;
      recordCounts["Weight"] = count;
      console.log(`\n✓ Weight: ${count} records`);
      if (weight.samples && weight.samples.length > 0) {
        console.log("  First 50 records:");
        weight.samples.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Weight error:", error);
      recordCounts["Weight"] = 0;
    }

    // Test 12: Fetch Workouts
    try {
      const workouts = await Health.queryWorkouts({
        startDate,
        endDate,
        limit: 100,
      });
      const count = workouts.workouts?.length || 0;
      recordCounts["Workouts"] = count;
      console.log(`\n✓ Workouts: ${count} records`);
      if (workouts.workouts && workouts.workouts.length > 0) {
        console.log("  First 50 records:");
        workouts.workouts.slice(0, 50).forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Workouts error:", error);
      recordCounts["Workouts"] = 0;
    }

    // Test 13: Aggregated Steps (queryAggregated)
    try {
      const aggregated = await Health.queryAggregated({
        dataType: "steps",
        startDate,
        endDate,
        bucket: "day",
        aggregation: "sum",
      });
      const count = aggregated.samples?.length || 0;
      recordCounts["Aggregated Steps (by day)"] = count;
      console.log(`\n✓ Aggregated steps: ${count} days`);
      if (aggregated.samples && aggregated.samples.length > 0) {
        console.log("  All aggregated day records:");
        aggregated.samples.forEach((record, index) => {
          console.log(`  [${index + 1}]`, record);
        });
      }
    } catch (error) {
      console.error("✗ Aggregated steps error:", error);
      recordCounts["Aggregated Steps (by day)"] = 0;
    }

    // Summary of all record counts
    console.log("\n" + "=".repeat(60));
    console.log("📊 SUMMARY OF ALL RECORD COUNTS");
    console.log("=".repeat(60));

    let totalRecords = 0;
    Object.entries(recordCounts).forEach(([dataType, count]) => {
      console.log(`${dataType.padEnd(30)}: ${count} records`);
      totalRecords += count;
    });

    console.log("=".repeat(60));
    console.log(`TOTAL RECORDS: ${totalRecords}`);
    console.log("=".repeat(60));

    console.log("\n✅ All @capgo/capacitor-health v8.2.9 tests completed!");

    return {
      success: true,
      message: "All tests completed. Check console for details.",
    };
  } catch (error) {
    console.error("\n❌ @capgo/capacitor-health test failed:", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

/**
 * Compare data count from @capgo vs @perfood
 * Call this after testCapgoHealthV8() to verify data parity
 */
export const comparePluginData = async () => {
  console.log(
    "\n🔬 Comparing @capgo/capacitor-health vs @perfood/capacitor-healthkit..."
  );
  console.log("⚠️ Note: You need to implement @perfood fetching to compare");
  console.log("This is a placeholder for manual comparison");

  // TODO: Implement side-by-side comparison with @perfood
  // Fetch same date range from both plugins
  // Compare record counts and sample values

  return {
    message: "Manual comparison required - check both plugin outputs",
  };
};
